import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowRight,
  Building2,
  Camera,
  Check,
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  Clock3,
  Eye,
  EyeOff,
  Landmark,
  RotateCcw,
  ShieldCheck,
  UsersRound,
  X,
} from 'lucide-react';
import { API_BASE, baseapi } from '../baseapi';
import { SectorReadinessView } from '../Demoportal/SectorReadinessView';

const REFRESH_MS = 30000;
const P = 'darshan-evidence';
const img = (path: string) => `${API_BASE}${P}/${path}`;

const SEG_LABEL: Record<string, string> = {
  'V>T': 'Vinayaka Temple Q-Line Start → Tollgate',
  'K>T': 'Kummaripalem Q-Line Start → Tollgate',
  'T>X': 'Tollgate → Antharalayam Exit',
  'V>X': 'Vinayaka Temple Q-Line Start → Antharalayam Exit',
  'K>X': 'Kummaripalem Q-Line Start → Antharalayam Exit',
};
const SEG_ORDER = ['V>X', 'K>X', 'V>T', 'K>T', 'T>X'];
const dur = (minutes: number | null | undefined) => {
  if (minutes == null) return '—';
  const rounded = Math.round(minutes);
  return rounded >= 60 ? `${Math.floor(rounded / 60)} h ${String(rounded % 60).padStart(2, '0')} m` : `${rounded} m`;
};
const hh = (hour: number) => `${String(hour).padStart(2, '0')}:00 – ${String((hour + 1) % 24).padStart(2, '0')}:00`;
const todayIST = () => new Date(Date.now() + 5.5 * 3600e3).toISOString().slice(0, 10);
const currentHourIST = () => new Date(Date.now() + 5.5 * 3600e3).getUTCHours();
const card = 'rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-[#181b22]';
const muted = 'text-slate-600 dark:text-slate-300';

export const DarshanEvidencePage: React.FC = () => {
  const [day, setDay] = useState(todayIST());
  const [summary, setSummary] = useState<any>(null);
  const [pairs, setPairs] = useState<any[]>([]);
  const [status, setStatus] = useState<any>(null);
  const [open, setOpen] = useState<Set<number>>(new Set());
  const [seg, setSeg] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [reviewQueueVisible, setReviewQueueVisible] = useState(false);
  const [big, setBig] = useState<string | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [summaryResult, pairsResult, statusResult] = await Promise.all([
        baseapi.get(`${P}/summary`, { params: { day } }),
        baseapi.get(`${P}/pairs`, { params: { day, all: showAll ? 1 : 0 } }),
        baseapi.get(`${P}/status`),
      ]);
      setSummary(summaryResult.data);
      setPairs(pairsResult.data);
      setStatus(statusResult.data);
      setErr(null);
    } catch {
      setErr('Darshan journey evidence is temporarily unavailable.');
    }
  }, [day, showAll]);

  useEffect(() => {
    load();
    const timer = window.setInterval(load, REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [load]);

  const review = async (pair: any, decision: 'confirmed' | 'rejected' | 'auto') => {
    setBusy(pair.id);
    try {
      await baseapi.post(`${P}/pairs/${pair.id}/review`, { status: decision });
      if (decision === 'confirmed') setOpen(current => new Set(current).add(pair.entry.hour));
      await load();
    } catch (e: any) {
      setErr(e?.response?.status === 401 ? 'Sign in to confirm or reject an identity.' : 'The review could not be saved.');
    } finally {
      setBusy(null);
    }
  };

  const hours = useMemo(() => [...(summary?.hours || [])].reverse(), [summary]);
  const live = (status?.cameras || []).filter((camera: any) => camera.fps > 0).length;

  const byHour = useMemo(() => {
    const grouped: Record<number, any[]> = {};
    for (const pair of pairs) {
      if (pair.same_person) (grouped[pair.entry.hour] ||= []).push(pair);
    }
    for (const key in grouped) {
      grouped[key].sort((a, b) =>
        (a.review === 'confirmed' ? 0 : 1) - (b.review === 'confirmed' ? 0 : 1)
        || SEG_ORDER.indexOf(a.seg) - SEG_ORDER.indexOf(b.seg)
        || a.entry.ts - b.entry.ts,
      );
    }
    return grouped;
  }, [pairs]);

  const pairsById = useMemo(() => new Map(pairs.map(pair => [pair.id, pair])), [pairs]);

  const allToCheck = useMemo(() => pairs.filter(pair => pair.needs_review), [pairs]);
  const toCheck = useMemo(() => allToCheck.filter(pair => !seg || pair.seg === seg).sort((a, b) => b.sim - a.sim), [allToCheck, seg]);
  const visibleChecks = useMemo(() => pairs
    .filter(pair => (pair.needs_review || (showAll && pair.identity_status === 'candidate')) && (!seg || pair.seg === seg))
    .sort((a, b) => b.sim - a.sim), [pairs, seg, showAll]);
  const displayHours = hours;

  const evidenceText = (hour: any, line: 'V' | 'K') => {
    const total = hour?.darshan?.[line];
    if (!total) return 'No measured or estimated journey time yet';
    if (total.estimated) {
      return total.basis?.includes('segments')
        ? `${total.n || 0} verified segment matches used for estimate`
        : `${total.n || 0} verified complete journeys used for estimate`;
    }
    if (total.basis === 'same_person') {
      return `${total.n} verified complete ${total.n === 1 ? 'journey' : 'journeys'}`;
    }
    const firstCount = hour.segments?.[`${line}>T`]?.confirmed || 0;
    const finalCount = hour.segments?.['T>X']?.confirmed || 0;
    return `${firstCount} + ${finalCount} verified segment matches`;
  };

  const toggleHour = (hour: number) => setOpen(current => {
    const next = new Set(current);
    if (next.has(hour)) next.delete(hour); else next.add(hour);
    return next;
  });

  const Pic: React.FC<{ side: any; title: string }> = ({ side, title }) => (
    <div className="min-w-0 flex-1">
      <div className="mb-1.5 flex items-baseline justify-between gap-2">
        <span className="truncate text-sm font-bold">{title} · {side.label}</span>
        <span className="font-mono text-lg font-black tabular-nums text-blue-700 dark:text-blue-300">{side.time}</span>
      </div>
      <div className="flex gap-1.5">
        <img src={img(side.face)} alt="Face crop" loading="lazy" className="h-20 w-20 rounded-md border border-slate-200 bg-slate-100 object-cover sm:h-24 sm:w-24 dark:border-slate-700 dark:bg-black" />
        <img src={img(side.body)} alt="Person crop" loading="lazy" className="h-20 w-auto max-w-[5.5rem] rounded-md border border-slate-200 bg-slate-100 object-cover sm:h-24 dark:border-slate-700 dark:bg-black" />
        <button type="button" onClick={() => setBig(img(side.frame))} className="min-w-0 flex-1 cursor-zoom-in" title="Open full camera frame">
          <img src={img(side.frame)} alt="Camera frame" loading="lazy" className="h-20 w-full rounded-md border border-slate-200 bg-slate-100 object-cover sm:h-24 dark:border-slate-700 dark:bg-black" />
        </button>
      </div>
    </div>
  );

  const Pair: React.FC<{ p: any }> = ({ p }) => {
    const badge = p.identity_status === 'conflict'
      ? ['Possible duplicate match · Review required', 'bg-rose-600 text-white']
      : p.identity_status === 'confirmed'
        ? ['Confirmed same person', 'bg-emerald-600 text-white']
        : p.identity_status === 'automatic'
          ? ['Automatically verified same person', 'bg-emerald-50 text-emerald-800 ring-1 ring-emerald-300 dark:bg-emerald-950/40 dark:text-emerald-200']
          : p.review === 'rejected'
            ? ['Different Person', 'bg-rose-600 text-white']
            : p.needs_review
              ? ['Needs review · Not included in journey times', 'bg-amber-50 text-amber-800 dark:bg-amber-950/30 dark:text-amber-200']
              : ['Possible match · Not included in journey times', 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200'];
    return (
      <article className={`${card} p-3 ${p.review === 'confirmed' ? 'ring-1 ring-emerald-500/50' : ''} ${p.review === 'rejected' ? 'opacity-60' : ''}`}>
        <div className="mb-2.5 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="text-base font-extrabold">{SEG_LABEL[p.seg] || p.seg}</span>
            <span className={`rounded px-2 py-1 text-xs font-bold ${badge[1]}`}>{badge[0]}</span>
          </div>
          <div className="flex items-baseline gap-2">
            <span className={`text-sm ${muted}`}>Face match {Math.round(p.sim * 100)}%{p.margin != null ? ` · separation ${Math.round(p.margin * 100)}%` : ''}</span>
            <span className="font-mono text-2xl font-black tabular-nums">{dur(p.minutes)}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Pic side={p.entry} title="First seen" />
          <ArrowRight className="h-5 w-5 shrink-0 text-slate-500" />
          <Pic side={p.exit} title="Seen again" />
        </div>
        <div className="mt-2.5 flex justify-end gap-2">
          {p.review && !p.identity_conflict ? (
            <button type="button" disabled={busy === p.id} onClick={() => review(p, 'auto')} className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-bold hover:bg-slate-50 dark:border-slate-600 dark:hover:bg-white/5">
              <RotateCcw className="mr-1 inline h-4 w-4" /> Undo
            </button>
          ) : <>
            <button type="button" disabled={busy === p.id} onClick={() => review(p, 'rejected')} className="rounded-lg border border-rose-300 px-3 py-2 text-sm font-bold text-rose-800 hover:bg-rose-50 dark:text-rose-200">
              <X className="mr-1 inline h-4 w-4" /> Different Person
            </button>
            <button type="button" disabled={busy === p.id} onClick={() => review(p, 'confirmed')} className="rounded-lg bg-emerald-700 px-3 py-2 text-sm font-extrabold text-white hover:bg-emerald-800">
              <Check className="mr-1 inline h-4 w-4" /> Confirm Same Person
            </button>
          </>}
        </div>
      </article>
    );
  };

  const JourneyNode: React.FC<{ icon: React.ReactNode; label: string }> = ({ icon, label }) => (
    <div className="flex min-h-16 flex-1 items-center justify-center gap-3 rounded-xl border border-slate-200 bg-white/90 px-4 py-3 text-center shadow-sm dark:border-slate-700 dark:bg-[#181b22]">
      <span className="text-slate-700 dark:text-slate-200">{icon}</span>
      <span className="text-base font-extrabold leading-tight text-slate-900 dark:text-white">{label}</span>
    </div>
  );

  const JourneyPanel: React.FC<{ line: 'V' | 'K' }> = ({ line }) => {
    const isVinayaka = line === 'V';
    const isToday = day === todayIST();
    const currentHour = summary?.hours?.find((item: any) => item.hour === currentHourIST());
    const currentEstimate = currentHour?.darshan?.[line];
    const total = isToday ? (currentEstimate || summary?.daily?.[line]) : summary?.daily?.[line];
    const showingCurrentEstimate = isToday && Boolean(currentEstimate);
    const firstLeg = summary?.daily_segments?.[`${line}>T`];
    const finalLeg = summary?.daily_segments?.['T>X'];
    const firstAverage = firstLeg?.avg;
    const finalAverage = finalLeg?.avg;
    const badgeText = !total
      ? 'Waiting for verified journey evidence'
      : `${showingCurrentEstimate ? 'Current estimate' : 'Selected-date average'} · ${total.n} verified ${total.basis?.includes('segments') ? 'segment matches' : total.n === 1 ? 'journey' : 'journeys'}`;
    return (
      <section className={`rounded-2xl border p-5 shadow-sm sm:p-6 ${isVinayaka
        ? 'border-sky-200 bg-sky-50/75 dark:border-sky-900 dark:bg-sky-950/20'
        : 'border-emerald-200 bg-emerald-50/75 dark:border-emerald-900 dark:bg-emerald-950/20'}`}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-center gap-4">
            <div className={`flex h-16 w-16 shrink-0 items-center justify-center rounded-full ${isVinayaka ? 'bg-sky-100 text-sky-950 dark:bg-sky-900/60 dark:text-sky-100' : 'bg-emerald-100 text-emerald-950 dark:bg-emerald-900/60 dark:text-emerald-100'}`}>
              {isVinayaka ? <Landmark className="h-9 w-9" /> : <Building2 className="h-9 w-9" />}
            </div>
            <div>
              <h2 className="text-2xl font-black tracking-tight text-[#7c0628] dark:text-rose-200">{isVinayaka ? 'Vinayaka Temple Queue' : 'Kummaripalem Queue'}</h2>
              <p className={`mt-0.5 text-base font-semibold ${muted}`}>Queue starting point to Antharalayam exit</p>
            </div>
          </div>
          <span className={`rounded-full px-3 py-2 text-sm font-extrabold ${total ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-100' : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200'}`}>
            <ShieldCheck className="mr-1 inline h-5 w-5" /> {badgeText}
          </span>
        </div>

        <div className="mt-4 text-center">
          <div className="text-xl font-extrabold text-slate-900 dark:text-white">{showingCurrentEstimate ? 'Estimated Darshan Time' : 'Average Darshan Time'}</div>
          <div className="mt-1 text-5xl font-black tracking-tight text-blue-700 sm:text-6xl dark:text-blue-300">{dur(total?.minutes)}</div>
          <div className={`mt-2 text-sm font-semibold ${muted}`}>{total
            ? showingCurrentEstimate
              ? `Current estimate based on recent verified journey evidence · updates as new journeys finish`
              : `Average of all verified complete journeys on the selected date · ${dur(total.min)} to ${dur(total.max)}`
            : 'No verified complete journey is available for this date'}</div>
          {total && <div className={`mt-1 text-xs font-semibold ${muted}`}>Each devotee was verified at both the queue entry and the Antharalayam exit.</div>}
        </div>

        <div className="mt-7 grid grid-cols-[1fr_auto_1fr_auto_1fr] items-center gap-3">
          <JourneyNode icon={<UsersRound className="h-7 w-7" />} label="Queue Entry" />
          <ArrowRight className="h-7 w-7 text-slate-700 dark:text-slate-200" />
          <JourneyNode icon={<Landmark className="h-7 w-7" />} label="Tollgate" />
          <ArrowRight className="h-7 w-7 text-slate-700 dark:text-slate-200" />
          <JourneyNode icon={<Building2 className="h-7 w-7" />} label="Antharalayam Exit" />
        </div>
        <div className="mt-3 grid grid-cols-2 gap-8 text-center">
          <div>
            <div className={`font-black text-blue-700 dark:text-blue-300 ${firstAverage == null ? 'text-base' : 'text-2xl'}`}>{firstAverage == null ? 'Not enough verified matches' : dur(firstAverage)}</div>
            <div className={`text-sm font-semibold ${muted}`}>Queue Entry → Tollgate · average</div>
          </div>
          <div>
            <div className={`font-black text-blue-700 dark:text-blue-300 ${finalAverage == null ? 'text-base' : 'text-2xl'}`}>{finalAverage == null ? 'Not enough verified matches' : dur(finalAverage)}</div>
            <div className={`text-sm font-semibold ${muted}`}>Tollgate → Antharalayam Exit · average</div>
          </div>
        </div>
        <div className={`mt-2 text-center text-xs font-semibold ${muted}`}>Route-segment averages use all verified segment matches and may not add exactly to the complete-journey average.</div>
      </section>
    );
  };

  return (
    <main className="min-h-screen w-full space-y-4 bg-[#f4f6f9] p-3 font-sans text-slate-950 sm:p-4 lg:p-5 dark:bg-[#111215] dark:text-white">
      <header className={`${card} flex flex-wrap items-center justify-between gap-4 px-5 py-4`}>
        <div>
          <h1 className="text-2xl font-black tracking-tight text-[#84052c] sm:text-3xl dark:text-rose-200">Darshan Time from Queue Entry to Temple Exit</h1>
          <p className={`mt-1 text-base font-semibold ${muted}`}>Current darshan-time estimate for each queue. Only verified same-person evidence is used.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <span className={`rounded-lg border px-4 py-2.5 text-sm font-extrabold ${status?.paused || status?.throttled
            ? 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200'
            : live === (status?.cameras?.length || 5)
              ? 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-200'
              : 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200'}`}>
            <Camera className="mr-1 inline h-5 w-5" /> {!status ? 'Checking cameras…' : status.paused
              ? 'Journey matching temporarily paused'
              : status.throttled
                ? `${live} of ${status.cameras.length} cameras online · reduced capture rate`
                : `${live} of ${status.cameras.length} evidence cameras online`}
          </span>
          <input type="date" value={day} max={todayIST()} onChange={event => { setDay(event.target.value); setOpen(new Set()); }} className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-extrabold dark:border-slate-600 dark:bg-[#181b22]" />
        </div>
      </header>

      {err && <div className="rounded-xl border border-rose-300 bg-rose-50 px-4 py-3 text-sm font-bold text-rose-800 dark:border-rose-800 dark:bg-rose-950/30 dark:text-rose-200">{err}</div>}

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <JourneyPanel line="V" />
        <JourneyPanel line="K" />
      </div>

      <SectorReadinessView embedded />

      <section className={`${card} overflow-hidden`}>
        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 border-b border-slate-200 px-5 py-4 dark:border-slate-700">
          <h2 className="text-2xl font-black tracking-tight text-[#84052c] dark:text-rose-200"><Clock3 className="mr-2 inline h-7 w-7" />Darshan Time by Entry Hour</h2>
          <p className={`text-base font-semibold ${muted}`}>The hour shows when devotees first entered the queue. Select a row to view the supporting photos.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] border-collapse text-base">
            <thead>
              <tr className="bg-slate-50 text-left text-sm font-black text-slate-800 dark:bg-slate-900/40 dark:text-slate-100">
                <th className="px-5 py-3.5">Queue Entry Time</th>
                <th className="px-5 py-3.5">Vinayaka Entry → Exit</th>
                <th className="px-5 py-3.5">Kummaripalem Entry → Exit</th>
                <th className="px-5 py-3.5">Evidence Used</th>
                <th className="w-14 px-5 py-3.5"><span className="sr-only">Expand</span></th>
              </tr>
            </thead>
            <tbody>
              {displayHours.length === 0 && (
                <tr><td colSpan={5} className={`px-5 py-10 text-center text-base font-semibold ${muted}`}>No validated journeys are available for this date yet.</td></tr>
              )}
              {displayHours.map((hour: any) => {
                const isOpen = open.has(hour.hour);
                const exactEvidence = byHour[hour.hour] || [];
                const supportingIds = new Set<number>();
                for (const line of ['V', 'K']) {
                  for (const id of (hour.darshan?.[line]?.evidence_pair_ids || []).slice(0, 2)) supportingIds.add(id);
                }
                for (const key of ['V>T', 'K>T', 'T>X']) {
                  if (hour.segments?.[key]?.avg == null) {
                    for (const id of (summary?.daily_segments?.[key]?.evidence_pair_ids || []).slice(0, 2)) supportingIds.add(id);
                  }
                }
                const exactIds = new Set(exactEvidence.map(pair => pair.id));
                const supportingEvidence = [...supportingIds]
                  .map(id => pairsById.get(id))
                  .filter((pair: any) => pair?.same_person && !exactIds.has(pair.id));
                const evidence = [...exactEvidence, ...supportingEvidence];
                const totalCell = (line: 'V' | 'K') => {
                  const total = hour.darshan?.[line];
                  if (!total) return <span className={muted}>Not available</span>;
                  return <>
                    <div className={`text-xl font-black ${total.estimated ? 'text-amber-700 dark:text-amber-300' : 'text-blue-700 dark:text-blue-300'}`}>{dur(total.minutes)}</div>
                    <div className={`mt-0.5 text-xs font-bold ${muted}`}>Average Darshan Time</div>
                  </>;
                };
                return (
                  <React.Fragment key={hour.hour}>
                    <tr onClick={() => toggleHour(hour.hour)} className={`cursor-pointer border-t border-slate-200 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-white/5 ${isOpen ? 'bg-blue-50/60 dark:bg-blue-950/20' : ''}`}>
                      <td className="px-5 py-4 font-mono text-lg font-black tabular-nums"><ChevronRight className={`mr-2 inline h-5 w-5 transition-transform ${isOpen ? 'rotate-90' : ''}`} />{hh(hour.hour)}</td>
                      <td className="px-5 py-4">{totalCell('V')}</td>
                      <td className="px-5 py-4">{totalCell('K')}</td>
                      <td className="px-5 py-4">
                        <div className="font-extrabold text-slate-900 dark:text-white">Vinayaka: {evidenceText(hour, 'V')}</div>
                        <div className={`mt-1 text-sm font-bold ${muted}`}>Kummaripalem: {evidenceText(hour, 'K')}</div>
                      </td>
                      <td className="px-5 py-4 text-right">{isOpen ? <ChevronDown className="inline h-5 w-5" /> : <ChevronRight className="inline h-5 w-5" />}</td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-slate-50/70 dark:bg-slate-900/30">
                        <td colSpan={5} className="p-4">
                          <div className="mb-4 grid gap-3 md:grid-cols-3">
                            {[
                              ['Vinayaka Entry → Tollgate', 'V>T'],
                              ['Kummaripalem Entry → Tollgate', 'K>T'],
                              ['Tollgate → Antharalayam Exit', 'T>X'],
                            ].map(([label, key]: any) => {
                              const measured = hour.segments?.[key];
                              const estimated = measured?.avg == null && summary?.daily_segments?.[key]?.avg != null;
                              const value = estimated ? summary?.daily_segments?.[key] : measured;
                              return <div key={label} className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-[#181b22]">
                                <div className={`text-sm font-extrabold ${muted}`}>{label}</div>
                                <div className={`mt-1 text-2xl font-black ${estimated ? 'text-amber-700 dark:text-amber-300' : 'text-blue-700 dark:text-blue-300'}`}>{value?.avg == null ? 'Not available' : dur(value.avg)}</div>
                                <div className={`mt-1 text-xs font-semibold ${estimated ? 'text-amber-700 dark:text-amber-300' : muted}`}>
                                  {estimated && value?.avg != null
                                    ? `Estimated from historical data · ${value.n || 0} verified`
                                    : `Average from ${value?.confirmed || 0} verified same-person ${value?.confirmed === 1 ? 'match' : 'matches'}`}
                                </div>
                              </div>;
                            })}
                          </div>
                          {evidence.length > 0
                            ? <>
                              {supportingEvidence.length > 0 && <div className="mb-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm font-bold text-blue-900 dark:border-blue-800 dark:bg-blue-950/30 dark:text-blue-100">
                                Verified supporting photos from completed journeys are shown below. Photos from another entry hour are used only when this hour does not yet have enough completed journeys.
                              </div>}
                              <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">{evidence.map(pair => <Pair key={pair.id} p={pair} />)}</div>
                            </>
                            : <div className={`rounded-xl border border-slate-200 bg-white px-4 py-6 text-center font-semibold dark:border-slate-700 dark:bg-[#181b22] ${muted}`}>No verified same-person photos are available for this entry hour.</div>}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section className={card}>
        <button type="button" onClick={() => setReviewQueueVisible(visible => !visible)} aria-expanded={reviewQueueVisible} aria-controls="identity-review-queue" className="flex w-full flex-wrap items-center gap-4 px-5 py-4 text-left">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-sky-100 text-blue-700 dark:bg-sky-900/50 dark:text-blue-200"><ClipboardCheck className="h-7 w-7" /></span>
          <span className="min-w-0 flex-1">
            <span className="block text-xl font-black text-[#84052c] dark:text-rose-200">Possible Matches Needing Review · {allToCheck.length}</span>
            <span className={`mt-0.5 block text-sm font-semibold ${muted}`}>These possible same-person matches need confirmation. They are not included in the times shown above.</span>
          </span>
          <span className="rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm font-extrabold shadow-sm dark:border-slate-600 dark:bg-[#181b22]">
            {reviewQueueVisible ? <><EyeOff className="mr-1 inline h-4 w-4" /> Hide Matches</> : <><Eye className="mr-1 inline h-4 w-4" /> Show Matches to Review</>} <ChevronRight className={`ml-2 inline h-5 w-5 transition-transform ${reviewQueueVisible ? 'rotate-90' : ''}`} />
          </span>
        </button>

        {reviewQueueVisible && (
          <div id="identity-review-queue" className="space-y-3 border-t border-slate-200 p-4 dark:border-slate-700">
            <div className="flex flex-wrap items-center gap-2">
              {['', 'V>T', 'K>T', 'T>X', 'V>X', 'K>X'].map(key => (
                <button type="button" key={key || 'all'} onClick={() => setSeg(key)} className={`rounded-lg border px-3 py-2 text-sm font-bold ${seg === key ? 'border-blue-700 bg-blue-700 text-white' : 'border-slate-300 bg-white dark:border-slate-600 dark:bg-[#181b22]'}`}>
                  {key ? SEG_LABEL[key] : 'All Routes'}
                </button>
              ))}
              <label className="ml-1 flex items-center gap-2 text-sm font-bold">
                <input type="checkbox" checked={showAll} onChange={event => setShowAll(event.target.checked)} /> Also show weaker possible matches
              </label>
              <span className={`ml-auto text-sm font-semibold ${muted}`}>{toCheck.length} need review for the selected route</span>
            </div>
            <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
              {visibleChecks.map(pair => <Pair key={pair.id} p={pair} />)}
            </div>
            {visibleChecks.length === 0 && summary && <div className={`rounded-xl border border-slate-200 bg-slate-50 px-4 py-8 text-center font-semibold dark:border-slate-700 dark:bg-slate-900/30 ${muted}`}>No possible matches currently need review.</div>}
          </div>
        )}
      </section>

      {status && (
        <div className={`px-1 text-sm font-semibold leading-relaxed ${muted}`}>
          {status.cameras.map((camera: any) => `${camera.label}: ${camera.fps > 0 ? `${camera.fps} frames/s · ${camera.people_seen} people seen` : camera.state}`).join('  ·  ')}
        </div>
      )}

      {big && (
        <button type="button" aria-label="Close full camera frame" className="fixed inset-0 z-50 flex cursor-zoom-out items-center justify-center bg-black/85 p-4" onClick={() => setBig(null)}>
          <img src={big} alt="Full camera frame" className="max-h-full max-w-full rounded-lg" />
        </button>
      )}
    </main>
  );
};

export default DarshanEvidencePage;
